import { useMediaQuery } from '@uidotdev/usehooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, IconButton, Variant } from '@indicia/component-library';
import useBem from '@indicia/use-bem';

import getFileFromStorage from '../../../shared/utils/DownloadFile';
import useSurveyContext from '../store/UseSurveyContext';

import './StartSurvey.scss';

const StartSurvey = () => {
  const { activationCode } = useParams();
  const navigator = useNavigate();
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const { t, i18n } = useTranslation();
  const isSmallDevice = useMediaQuery('only screen and (max-width : 998px)');
  const {
    window: {
      __RUNTIME_CONFIG__: {
        ALGEMENE_VOORWAARDEN_URL_EN,
        ALGEMENE_VOORWAARDEN_URL,
        ALGEMENE_VOORWAARDEN_NAME_EN,
        ALGEMENE_VOORWAARDEN_NAME,
        PRIVACY_STATEMENT_URL_EN,
        PRIVACY_STATEMENT_URL,
        PRIVACY_STATEMENT_NAME_EN,
        PRIVACY_STATEMENT_NAME,
      },
    },
  } = globalThis;

  const [step, setStep] = useState<'intro' | 'consent'>('intro');

  const { bemClassName, bemClassNames } = useBem('survey-start');
  const { setEmailAddress, removeAnswers, updateActivationCode } = useSurveyContext();

  useEffect(() => {
    updateActivationCode(activationCode ?? sessionStorage.getItem('gezondleven-activation-code'));
  });

  const handleStartSurvey = useCallback(() => {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    removeAnswers();
    navigator('/survey');
  }, []);

  const downloadAlgemeneVoorwaarden = useCallback(() => {
    const isEn = i18n?.language?.toLowerCase()?.startsWith('en');
    const url = isEn ? ALGEMENE_VOORWAARDEN_URL_EN : ALGEMENE_VOORWAARDEN_URL;
    const name = isEn ? ALGEMENE_VOORWAARDEN_NAME_EN : ALGEMENE_VOORWAARDEN_NAME;

    getFileFromStorage(url, name);
  }, [i18n?.language]);

  const downloadPrivacyStatement = useCallback(() => {
    const isEn = i18n?.language?.toLowerCase()?.startsWith('en');
    const url = isEn ? PRIVACY_STATEMENT_URL_EN : PRIVACY_STATEMENT_URL;
    const name = isEn ? PRIVACY_STATEMENT_NAME_EN : PRIVACY_STATEMENT_NAME;

    getFileFromStorage(url, name);
  }, [i18n?.language]);

  const goToConsent = () => {
    setStep('consent');
    setEmailAddress(emailInputRef?.current?.value ?? '');
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  };

  return (
    <section className={bemClassNames([bemClassName(), 'background-image'])}>
      <div className="container">
        <div className={bemClassNames([bemClassName('content'), bemClassName('content', 'visible', step === 'intro')])}>
          <h1>{t('start.title')}</h1>
          <div className={bemClassName('scroll')}>
            <p className="intro">{t('start.subtitle')}</p>
            <Trans
              i18nKey="start.text"
              components={{ p: <p />, b: <strong />, br: <br />, ul: <ul />, li: <li /> }}
            />
          </div>
          <div className={bemClassNames([bemClassName('actions'), bemClassName('actions', 'mobile')])}>
            <div className="form-item">
              <label htmlFor="email">{t('start.email-label')}</label>
              <input
                type="text"
                placeholder={t('start.email-placeholder')!}
                ref={emailInputRef}
                id="email"
              />
            </div>
            <IconButton
              onClick={() => goToConsent()}
              type="button"
              iconName="arrow-right"
            >
              {t('survey.start')}
            </IconButton>
          </div>
          {!isSmallDevice && step === 'intro' && (
            <div className={bemClassNames([bemClassName('actions'), bemClassName('actions', 'desktop')])}>
              <div className="form-item">
                <label htmlFor="email-desktop">{t('start.email-label')}</label>
                <input
                  type="text"
                  placeholder={t('start.email-placeholder')!}
                  ref={emailInputRef}
                  id="email-desktop"
                />
              </div>
              <IconButton
                onClick={() => goToConsent()}
                type="button"
                iconName="arrow-right"
              >
                {t('survey.start')}
              </IconButton>
            </div>
          )}
        </div>
        <div
          className={bemClassNames([
            bemClassName('content'),
            bemClassName('content', 'consent'),
            bemClassName('content', 'visible', step === 'consent'),
          ])}
        >
          <h1>{t('toestemmingsverklaring.title')}</h1>
          <div className={bemClassName('scroll')}>
            <Trans
              i18nKey="toestemmingsverklaring.content"
              components={{
                p: <p />,
                ul: <ul />,
                li: <li />,
                b: <strong />,
                privacyStatementLink: (
                  <Button
                    onClick={() => downloadPrivacyStatement()}
                    variant={Variant.Plain}
                  >
                    {t('generic.privacy-statement-small')!}
                  </Button>
                ),
                algemeneVoorwaardenLink: (
                  <Button
                    onClick={() => downloadAlgemeneVoorwaarden()}
                    variant={Variant.Plain}
                  >
                    {t('generic.algemene-voorwaarden-small')!}
                  </Button>
                ),
              }}
            />
          </div>
          <div className={bemClassNames([bemClassName('actions'), bemClassName('actions', 'mobile')])}>
            <IconButton
              onClick={() => handleStartSurvey()}
              type="button"
              iconName="arrow-right"
            >
              {t('toestemmingsverklaring.accept')}
            </IconButton>
          </div>
        </div>
        {!isSmallDevice && (
          <div className={bemClassName('image')}>
            <img
              alt=""
              src="images/start.png"
            />
            {step === 'consent' && (
              <div className={bemClassNames([bemClassName('actions'), bemClassName('actions', 'desktop')])}>
                <IconButton
                  onClick={() => handleStartSurvey()}
                  type="button"
                  iconName="arrow-right"
                >
                  {t('toestemmingsverklaring.accept')}
                </IconButton>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default StartSurvey;
